'use client'

/**
 * Policies v3 — the external auditor's session, kept in this browser tab (G1).
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform; the Policies module puts an
 * organisation's own policy PDFs in front of its own staff and keeps the proof. An outside auditor, invited by that
 * organisation, signs in with an emailed one-time code and reads the proof read-only. The auditor is NOT a workspace
 * user: what the code earns is an opaque session token (at most 8 hours, never past the end of the access), sent back
 * as `X-Policy-Audit-Token`. It lives in `sessionStorage` (this tab only; closing the tab ends it) and in memory, and it
 * is never written to the admin session's storage nor read from it.
 *
 * Browser storage can throw (a private window, blocked site data) and can come back empty: every read and write is in a
 * try/catch and the memory copy keeps the tab working, so the page renders correctly without the storage.
 *
 * Besides the token the tab remembers WHICH invitation signed in (`grantId` and the link's `sig`), so a lapsed session
 * can offer "Sign in again" with the same link, and who the access belongs to (`orgName`) so the "Your access has ended"
 * page can say whose organisation to ask. The signature alone opens nothing: signing in still needs a code sent to the
 * invited address.
 */
import { useSyncExternalStore } from 'react'

const SESSION_KEY = 'hf-policy-audit:session'
const LINK_KEY = 'hf-policy-audit:link'

interface StoredSession {
  token: string
  /** The instant the API says the session ends; null when it did not say. */
  expiresAt: string | null
}

export interface AuditLink {
  grantId: string
  sig: string
  /** The organisation that invited this auditor ("Infosec Ventures"), once known. */
  orgName: string | null
  /** Set when the API said the ACCESS (not just the session) is over. */
  endedAt: string | null
}

// ── Safe storage ────────────────────────────────────────────────────────────

function readStored(key: string): string | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (typeof window === 'undefined') return
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    /* storage is a convenience: the memory copy keeps the tab working */
  }
}

function parse<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

// ── The session ─────────────────────────────────────────────────────────────

let memorySession: StoredSession | null = null
let memoryLink: AuditLink | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of [...listeners]) listener()
}

function currentSession(): StoredSession | null {
  const stored = memorySession ?? parse<StoredSession>(readStored(SESSION_KEY))
  if (!stored || typeof stored.token !== 'string' || stored.token.length === 0) return null
  if (stored.expiresAt) {
    const ends = Date.parse(stored.expiresAt)
    if (!Number.isNaN(ends) && ends <= Date.now()) return null
  }
  return stored
}

/** The session token, or null when there is none or it has run out. */
export function getAuditorToken(): string | null {
  return currentSession()?.token ?? null
}

/** Keep a session the one-time code earned. */
export function setAuditorSession(token: string, expiresAt: string | null): void {
  memorySession = { token, expiresAt }
  writeStored(SESSION_KEY, JSON.stringify(memorySession))
  notify()
}

/** End the session in this tab (the link record stays, for "sign in again"). */
export function clearAuditorSession(): void {
  memorySession = null
  writeStored(SESSION_KEY, null)
  notify()
}

/** Call `listener` whenever the session appears or goes. Returns the way to stop. */
export function subscribeAuditorSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The session token as a value that re-renders its reader when it changes; null on the server. */
export function useAuditorToken(): string | null {
  return useSyncExternalStore(subscribeAuditorSession, getAuditorToken, () => null)
}

// ── The invitation link this tab signed in with ─────────────────────────────

/** What this tab knows of its invitation, or null. */
export function getAuditLink(): AuditLink | null {
  return memoryLink ?? parse<AuditLink>(readStored(LINK_KEY))
}

function saveLink(link: AuditLink): void {
  memoryLink = link
  writeStored(LINK_KEY, JSON.stringify(link))
}

/** Remember the invitation (its grant and signature) and, once known, the organisation's name. */
export function rememberAuditLink(link: { grantId: string; sig: string; orgName?: string | null }): void {
  const before = getAuditLink()
  const same = before?.grantId === link.grantId
  saveLink({
    grantId: link.grantId,
    sig: link.sig,
    orgName: link.orgName ?? (same ? before.orgName : null),
    endedAt: same ? before.endedAt : null,
  })
}

/** The API said this auditor's ACCESS is over. */
export function markAuditorAccessEnded(endedAt: string | null): void {
  const link = getAuditLink()
  saveLink({ grantId: link?.grantId ?? '', sig: link?.sig ?? '', orgName: link?.orgName ?? null, endedAt: endedAt ?? new Date().toISOString() })
}

/** Is this tab's access over, and whose organisation to ask? Null while it is not. */
export function getAuditorEnded(): { endedAt: string; orgName: string | null } | null {
  const link = getAuditLink()
  return link?.endedAt ? { endedAt: link.endedAt, orgName: link.orgName } : null
}

/** Forget the invitation too (a deliberate sign-out keeps it: the link in the e-mail is still good). */
export function forgetAuditLink(): void {
  memoryLink = null
  writeStored(LINK_KEY, null)
}

/** For specs: forget everything this tab knew. */
export function resetAuditorSessionForTests(): void {
  memorySession = null
  memoryLink = null
  writeStored(SESSION_KEY, null)
  writeStored(LINK_KEY, null)
  listeners.clear()
}
